import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { parseViewStateFiltersOptions, View } from '../../views'
import {
  Product,
  ProductField,
  ProductHistoricStockData,
  ProductScope,
  ProductSearchParams,
  ProductsListingCategories,
  ProductsListingPage,
} from '../product.model'
import { ProductsListingData } from '../models/products-listing.model'
import { Observable } from 'rxjs'
import { SuppliersService } from '../../suppliers'
import { ProductsListingRepository } from '../repositories/products-listing.repository'
import { BrandsService } from '../../brands'
import { ChannelsService } from '../../channels'
import { mergeById } from '../../../libs/filter.lib'
import { CategoriesService } from '../../categories'
import { ProductFamiliesService } from '../../product-families'
import { ProductsService } from '../products.service'
import { LocationsService } from '../../locations'
import { ManufacturersService } from '../../manufacturers'
import { TagsService } from '../../tags'
import { Attribute } from '../../attributes'
import { omit, uniq, uniqBy } from 'lodash'
import { ProductsRepository } from '../products.repository'

export interface ProductsFilterData {
  params?: ProductSearchParams
  count?: number
  scope?: ProductScope
}

interface ProductsListingState {
  filters?: ProductSearchParams
  customFilters?: ProductSearchParams
  data?: Product[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
  extData?: ProductsListingData
  dropdownOptions?: ProductsListingData
  searchString?: string
  searchKeys?: ProductSearchParams
  fields?: ProductField[]
  view?: View
  scope?: ProductScope
  attributes?: Attribute[]
  historicData?: ProductHistoricStockData
  categories?: ProductsListingCategories
  historicFiltered: boolean
}

const PRODUCTS_LISTING_INITIAL_STATE: ProductsListingState = {
  isLoading: false,
  isInitialized: false,
  historicFiltered: false,
}

@Injectable()
export class ProductsListingStore {
  constructor(
    private productsListingRepository: ProductsListingRepository,
    private brandsService: BrandsService,
    private categoriesService: CategoriesService,
    private channelsService: ChannelsService,
    private familiesService: ProductFamiliesService,
    private locationsService: LocationsService,
    private manufacturersService: ManufacturersService,
    private productsService: ProductsService,
    private tagsService: TagsService,
    private suppliersService: SuppliersService,
    private productsRepository: ProductsRepository,
  ) {}

  private state = signal<ProductsListingState>(PRODUCTS_LISTING_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly customFilters = computed(() => this.state().customFilters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    const customFilters = this.state().customFilters
    return (
      (!!filters && Object.keys(filters).length > 0) ||
      (!!customFilters && Object.keys(customFilters).length > 0)
    )
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly products = computed(() => this.state().data || [])
  readonly extData = computed(() => this.state().extData)
  readonly dropdownOptions = computed(() => this.state().dropdownOptions)
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly searchKeys = computed(() => this.state().searchKeys)
  readonly fields = computed(() => this.state().fields)
  readonly view = computed(() => this.state().view)
  readonly scope = computed(() => this.state().scope)
  readonly attributes = computed(() => this.state().attributes)
  readonly historicData = computed(() => this.state().historicData)
  readonly categories = computed(() => this.state().categories)
  readonly rootId = computed(() => this.categories()?.rootId)
  readonly currentCatId = computed(() => this.categories()?.currentId)
  readonly uncategorized = computed(() => !!this.categories()?.uncategorized)
  readonly historicFiltered = computed(() => !!this.state()?.historicFiltered)
  readonly scopeWarehouseId = computed(() => this.scope()?.warehouseId)

  // Effects

  async search(
    params?: ProductSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()
    const scope = this.scope()
    const historicData = this.historicData()

    return new Promise((resolve, reject) => {
      this.productsListingRepository
        .searchProducts$(
          {
            params: this.parseStateFilterParams(this.state(), params),
            fields: this.state().fields ?? [],
            scope,
            attributes: this.attributes(),
            historicData,
          },
          this.extData(),
          returnAll,
        )
        .subscribe({
          next: (data) => {
            this.setData(data)
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async searchLookup(event: {
    dataSourceKey: string
    q?: string
    dataKeyField?: string
    dataDisplayField?: string
    offset?: number
    limit?: number
  }): Promise<void> {
    type LookupKey = keyof ProductsListingData
    type ArrayElement<T> = T extends (infer U)[] ? U : never
    type LookupValue = ArrayElement<NonNullable<ProductsListingData[LookupKey]>>

    const queryKey = `${event.dataDisplayField ?? 'name'}:ct`
    const offset = event.offset ?? 0
    const limit = event.limit ?? 50
    const query = {
      offset,
      limit,
      ...(event.q ? { [queryKey]: event.q } : {}),
    } as Record<string, unknown>

    const serviceMap: Partial<
      Record<LookupKey, () => Observable<LookupValue[]>>
    > = {
      brands: () => this.brandsService.list$(query),
      categories: () => this.categoriesService.list$(query),
      channels: () => this.channelsService.list$(query),
      families: () => this.familiesService.list$(query),
      kits: () => this.productsService.list$(query ?? {}),
      locations: () => this.locationsService.list$(query),
      manufacturers: () => this.manufacturersService.list$(query),
      parents: () => this.productsService.list$(query ?? {}),
      tags: () => this.tagsService.list$({ ...query, target: 'products' }),
      suppliers: () => this.suppliersService.list$(query),
    }

    const search$ = serviceMap[event.dataSourceKey as LookupKey]
    if (!search$) return

    search$().subscribe((items) => {
      this.state.update((s) => {
        const key = event.dataSourceKey as LookupKey
        const prev = (s.dropdownOptions?.[key] ?? []) as { _id: string }[]
        const incoming = items as { _id: string }[]
        const merged = prev.length > 0 ? mergeById(prev, incoming) : incoming
        return {
          ...s,
          dropdownOptions: {
            ...s.extData,
            [event.dataSourceKey]: merged as LookupValue[],
          },
        }
      })
    })
  }

  async searchOrder(orderId: string) {
    return new Promise(() => {
      this.productsListingRepository.getOrderIds$(orderId).subscribe({
        next: (data) => {
          this.setSearchKeys({ _id: data })
        },
        error: (error) => {
          this.setError(error)
        },
      })
    })
  }

  async searchGoodsReceive(goodsReceiveId: string) {
    return new Promise(() => {
      this.productsListingRepository
        .getGoodsReceiveIds$(goodsReceiveId)
        .subscribe({
          next: (data) => {
            this.setSearchKeys({ _id: data })
          },
          error: (error) => {
            this.setError(error)
          },
        })
    })
  }

  async searchSupplierOrder(orderId: string) {
    return new Promise(() => {
      this.productsListingRepository.getSupplierOrderIds$(orderId).subscribe({
        next: (data) => {
          this.setSearchKeys({ _id: data })
        },
        error: (error) => {
          this.setError(error)
        },
      })
    })
  }

  async searchPickingList(pickingListId: string) {
    return new Promise(() => {
      this.productsListingRepository
        .getPickingListIds$(pickingListId)
        .subscribe({
          next: (data) => {
            this.setSearchKeys({ _id: data })
          },
          error: (error) => {
            this.setError(error)
          },
        })
    })
  }

  async searchVariants(product: Product) {
    return new Promise((resolve, reject) => {
      this.productsListingRepository.getVariantFamilyIds$(product).subscribe({
        next: (data) => {
          this.setFilters({ _id: data }, true)
          resolve(data.length > 0)
        },
        error: (error) => {
          this.setError(error)
          reject()
        },
      })
    })
  }

  async delete(product: Product): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.productsRepository
        .alert('PRODUCT_DELETION', product)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.productsService.delete$(product._id).subscribe({
              next: () => {
                this.productsRepository.notify({ code: 'PRODUCT_DELETED' })
                resolve(true)
              },
              error: (error: HttpErrorResponse) => {
                this.setError(error)
                resolve(false)
              },
            })
          }
        })
    })
  }

  // Reducers

  setData(data: ProductsListingPage) {
    const extData = this.extData()
    const newExtData = {
      attributes: uniqBy(
        [...(extData?.attributes ?? []), ...(data.extData?.attributes ?? [])],
        'code',
      ),
      brands: mergeById(extData?.brands, data.extData?.brands),
      categories: mergeById(extData?.categories, data.extData?.categories),
      channels: mergeById(extData?.channels, data.extData?.channels),
      families: mergeById(extData?.families, data.extData?.families),
      kits: mergeById(extData?.kits, data.extData?.kits),
      locations: mergeById(extData?.locations, data.extData?.locations),
      manufacturers: mergeById(
        extData?.manufacturers,
        data.extData?.manufacturers,
      ),
      parents: mergeById(extData?.parents, data.extData?.parents),
      suppliers: mergeById(extData?.suppliers, data.extData?.suppliers),
      tags: mergeById(extData?.tags, data.extData?.tags),
    }
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      extData: newExtData,
      dropdownOptions: newExtData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: ProductSearchParams, replaceFilters?: boolean) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters:
        replaceFilters === true
          ? filters
          : { ...filters, q: this.state().filters?.q },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  setView(view: View) {
    this.state.set({
      ...this.state(),
      view,
      fields: view.fields as ProductField[],
      customFilters: !view.filters
        ? {}
        : parseViewStateFiltersOptions<ProductSearchParams>(view.filters, {}),
    })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: ProductSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: ProductSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof ProductSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setFields(fields?: ProductField[]) {
    this.state.set({ ...this.state(), fields })
  }

  setSearchKeys(searchKeys?: ProductSearchParams) {
    const parsedKeys = searchKeys
      ? {
          ...searchKeys,
          SKU:
            searchKeys?.SKU !== undefined
              ? Array.isArray(searchKeys.SKU)
                ? uniq(searchKeys.SKU)
                : searchKeys.SKU
              : undefined,
          _id: searchKeys?._id
            ? Array.isArray(searchKeys._id)
              ? uniq(searchKeys._id)
              : searchKeys._id
            : undefined,
        }
      : undefined
    this.state.set({ ...this.state(), searchKeys: parsedKeys })
  }

  setHistoricFiltered(historicFiltered: boolean) {
    this.state.set({ ...this.state(), historicFiltered })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  setHistoricStockData(
    historicFiltered: boolean,
    historicData?: ProductHistoricStockData,
  ) {
    this.state.set({
      ...this.state(),
      historicData,
      historicFiltered,
    })
  }

  setAttributes(attributes?: Attribute[]) {
    this.state.set({
      ...this.state(),
      attributes,
    })
  }

  setScope(scope?: ProductScope) {
    const searchKeys = this.searchKeys()
    const filters = this.filters()
    const customFilters = this.customFilters()
    const stateScope = this.scope()
    const isInitialized = this.isInitialized()

    // If we have active searchKeys (supplier order search) or customFilters,
    // never trigger a reload - these represent active filtered searches
    const hasActiveSearch = !!(searchKeys || filters || customFilters)

    // Only check if scope changed if there's no active search
    // This prevents unnecessary reloads when scope hasn't actually changed
    const scopeChanged = hasActiveSearch
      ? false
      : stateScope?.supplierId !== scope?.supplierId ||
        stateScope?.warehouseId !== scope?.warehouseId ||
        stateScope?.catalogCode !== scope?.catalogCode ||
        stateScope?.locale !== scope?.locale

    const shouldReload = !hasActiveSearch && scopeChanged && isInitialized
    const prev = this.categories()
    this.state.set({
      ...this.state(),
      scope,
      categories: scope?.categoriesRootId
        ? {
            rootId: scope.categoriesRootId,
            currentId: prev?.currentId,
            uncategorized: prev?.uncategorized,
          }
        : this.categories(),
    })
    if (shouldReload) {
      this.search()
    }
  }

  setRootId(rootId: string) {
    const scope = this.scope()
    this.state.set({
      ...this.state(),
      categories: {
        rootId,
      },
      scope: scope
        ? {
            ...scope,
            categoriesRootId: rootId,
          }
        : scope,
    })
  }

  setCurrentRootId(currentId: string) {
    const categories = this.categories()
    this.state.set({
      ...this.state(),
      categories: {
        rootId: categories?.rootId,
        currentId,
      },
    })
  }

  toggleUncategorizedFlag(uncategorized: boolean) {
    const categories = this.categories()
    this.state.set({
      ...this.state(),
      categories: {
        rootId: categories?.rootId,
        uncategorized: uncategorized || undefined,
      },
    })
  }

  resetCategories() {
    const categories = this.categories()
    this.state.set({
      ...this.state(),
      categories: {
        rootId: categories?.rootId,
      },
    })
  }

  private parseStateFilterParams(
    state: ProductsListingState,
    filters?: ProductSearchParams,
  ): ProductSearchParams {
    //const cleanedStateFilters = state.filters || {} //removeRawDateFilters(state.filters || {})
    const cleanedFilters = filters || {} // removeRawDateFilters(filters || {})

    const cleanedStateFilters = Object.fromEntries(
      Object.entries(state.filters || {}).filter(([key, value]) => {
        if (value instanceof Date && !key.includes(':')) return false
        return true
      }),
    ) as ProductSearchParams

    const cleanedStateCustomFilters = Object.fromEntries(
      Object.entries(state.customFilters || {}).filter(([key, value]) => {
        if (value instanceof Date && !key.includes(':')) return false
        return true
      }),
    ) as ProductSearchParams

    let params: ProductSearchParams = {
      ...cleanedStateFilters,
      ...cleanedStateCustomFilters,
      ...cleanedFilters,
    }
    if (state.searchString) {
      params.q = state.searchString
    }
    if (state.searchKeys) {
      params = { ...params, ...state.searchKeys }
    }
    if (state.categories?.currentId) {
      params.categories = state.categories.currentId
    }
    if (state.categories?.uncategorized) {
      params['categories:ex'] = false
    }

    // Scope
    if (state.scope?.catalogCode) {
      params.catalogCode = state.scope.catalogCode
    }
    if (state.scope?.locale) {
      params.locale = state.scope.locale
    }
    if (state.scope?.supplierId) {
      params['suppliers._id'] = state.scope.supplierId
    }
    if (state.scope?.warehouseId) {
      params.warehouseId = state.scope.warehouseId
    }
    if (params.suppliers) {
      params['suppliers._id'] = params.suppliers
      params.suppliers = undefined
    }

    return this.normalizeAttributeKeys(params)
  }

  normalizeAttributeKeys(obj: ProductSearchParams): ProductSearchParams {
    const out: ProductSearchParams = {}

    for (const [k, v] of Object.entries(obj)) {
      if (k.startsWith('attributesData.')) {
        const normalized = `attributes.${k.slice('attributesData.'.length)}`
        if (!(normalized in out))
          out[normalized as keyof ProductSearchParams] = v as never
        continue
      }

      out[k as keyof ProductSearchParams] = v as never
    }

    return out
  }

  parseFilterData(selected: Product[]): ProductsFilterData {
    const scope = this.scope()
    if (selected.length) {
      return {
        params: { _id: selected.map((o) => o._id) },
        count: selected.length,
        scope,
      }
    }
    const totalCount = this.totalCount()
    const length = this.products()?.length
    return {
      params: omit(this.parseStateFilterParams(this.state()), [
        'limit',
        'offset',
        'sort',
        'order',
      ]),
      count: totalCount ?? length ?? 0,
      scope,
    }
  }
}
